"use client"

import styles from "@styles/TaskCard.module.css"
import { Task } from "@database/tasks/saveTasks"
import clsx from "clsx"
import SlackTaskCardContent from "./SlackTaskCardContent"
import EmailTaskCardContent from "./EmailTaskCardContent"
import MeetingTaskCardContent, {
  type MeetingTaskCardContentProps,
} from "./MeetingTaskCardContent"
import { CalendarEvent } from "@/app/helpers/google/calendar/fetchCalendarEvents"
import { useState, useEffect } from "react"
import { ClickAwayListener } from "@mui/material"

export interface TaskCardProps {
  task?: Task
  calendarEvent?: CalendarEvent
  isFirst?: boolean
}

export function TaskCard({ task, calendarEvent, isFirst }: TaskCardProps) {
  const [isMaximised, setIsMaximised] = useState(false)
  const [blurVisible, setBlurVisible] = useState(false)
  const [overlayHeight, setOverlayHeight] = useState<number | null>(null)

  useEffect(() => {
    if (isMaximised) {
      const scrollContainer = document.querySelector<HTMLElement>(
        "[data-scroll-container]",
      )
      setOverlayHeight(scrollContainer?.scrollHeight ?? null)
      const frame = requestAnimationFrame(() => setBlurVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setBlurVisible(false)
    setOverlayHeight(null)
  }, [isMaximised])

  let taskType = "email"

  if (calendarEvent) {
    taskType = "meeting"
  } else if (task.taskType === "respondToChatMessage") {
    taskType = "slack"
  } else if (task.taskType === "meeting" || task.resourceType === "calendar") {
    taskType = "meeting"
  }

  return (
    <>
      <ClickAwayListener
        onClickAway={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setIsMaximised(false)
        }}
      >
        <div
          className={clsx(
            styles.taskCard,
            isFirst && styles.first,
            isMaximised && styles.maximised,
          )}
          onClick={() => !isMaximised && setIsMaximised(true)}
        >
          {taskType === "slack" && (
            <SlackTaskCardContent
              task={task}
              isFirst={isFirst}
              isMaximised={isMaximised}
              onMaximisedClose={() => setIsMaximised(false)}
            />
          )}
          {taskType === "email" && (
            <EmailTaskCardContent
              task={task}
              isMaximised={isMaximised}
              onMaximisedClose={() => setIsMaximised(false)}
            />
          )}
          {taskType === "meeting" && calendarEvent && (
            <MeetingTaskCardContent
              calendarEvent={calendarEvent}
              isMaximised={isMaximised}
              onMaximisedClose={() => setIsMaximised(false)}
            />
          )}
        </div>
      </ClickAwayListener>
      {isMaximised && (
          <div
            className={clsx(
              styles.taskCardBlurBackground,
              blurVisible && styles.visible,
            )}
            style={{
              height: overlayHeight ?? "100vh",
            }}
          />
        )}
    </>
  )
}
